// @systeme: contrat
// La boîte de réception : les captures et les fiches proposées à valider.
import { z } from 'zod';
import { SchemaCadence } from './recurrences.js';
import { SchemaCategorieElement, SchemaChampFiche, SchemaModeCapture, SchemaStatutCapture, SchemaStatutFiche, SchemaTypeCapture, SchemaVisibilite, } from '../enumerations.js';
import { SchemaHeure, SchemaInstant, SchemaJour } from './elements.js';
import { SchemaDocument } from './documents.js';
import { SchemaFicheExtraite } from './llm/capture.js';
/** Une question posée en bas de l'écran, avec des réponses en boutons. */
export const SchemaQuestion = z.object({
    champ: SchemaChampFiche,
    texte: z.string(),
    options: z.array(z.object({
        libelle: z.string(),
        /** La valeur à renvoyer ; null pour « je ne sais pas encore ». */
        valeur: z.string().nullable(),
        /** « preciser » ouvre un sélecteur ; « plus_tard » crée quand même, à préciser. */
        genre: z.enum(['valeur', 'preciser', 'plus_tard']).default('valeur'),
    })),
});
/** Les données d'une fiche : ce que le modèle a extrait, plus ce que le serveur en a résolu. */
export const SchemaDonneesFiche = SchemaFicheExtraite.extend({
    /** Le jour résolu par le serveur (ou choisi par l'utilisatrice). Null = pas encore connu. */
    jour: SchemaJour.nullable(),
    /** La personne reconnue dans le foyer, si le prénom ou l'alias correspond. */
    personneId: z.uuid().nullable(),
    visibilite: SchemaVisibilite.default('espace'),
    /** Vrai si l'utilisatrice a répondu « je ne sais pas encore » à une question. */
    aPreciser: z.boolean().default(false),
    /** Ça revient ? La cadence en un clic, ou null pour « une fois ». */
    cadence: SchemaCadence.nullable().default(null),
    /** Pour une cadence hebdomadaire : les jours (lundi = 1). Vide = le jour de la fiche. */
    joursSemaine: z.array(z.number().int().min(1).max(7)).default([]),
    /** La date limite résolue par le serveur (« avant les vacances » → la veille du départ). */
    butoir: SchemaJour.nullable().default(null),
    /** Ce que la limite représente, en clair (« les vacances de la Toussaint »), pour l'afficher. */
    butoirNom: z.string().nullable().default(null),
});
export const SchemaFicheProposee = z.object({
    id: z.uuid(),
    captureId: z.uuid(),
    ordre: z.number().int(),
    donnees: SchemaDonneesFiche,
    champsManquants: z.array(SchemaChampFiche),
    questions: z.array(SchemaQuestion),
    statut: SchemaStatutFiche,
    elementId: z.uuid().nullable(),
    /** Pour une fiche « course » validée : l'article ajouté à la liste de courses. */
    articleId: z.uuid().nullable(),
    creeLe: SchemaInstant,
});
export const SchemaCapture = z.object({
    id: z.uuid(),
    type: SchemaTypeCapture,
    mode: SchemaModeCapture,
    statut: SchemaStatutCapture,
    transcription: z.string().nullable(),
    /** Un message pour l'utilisatrice quand ça n'a pas marché. */
    erreur: z.string().nullable(),
    dureeSecondes: z.number().nullable(),
    creeLe: SchemaInstant,
    fiches: z.array(SchemaFicheProposee),
    /** La photo, s'il y en a une. */
    documents: z.array(SchemaDocument),
});
export const SchemaBoiteDeReception = z.object({
    captures: z.array(SchemaCapture),
    /** Le nombre de fiches à valider, pour la pastille de l'écran Aujourd'hui. */
    nombreFiches: z.number().int(),
    /** Le nombre de captures en cours de traitement (« j'y travaille »). */
    nombreEnCours: z.number().int(),
});
export const SchemaCreationCaptureTexte = z.object({
    texte: z.string().trim().min(1, { error: 'Le message est vide.' }).max(5000),
    mode: SchemaModeCapture.default('normal'),
    /** Identifiant choisi par le client, pour ne pas envoyer deux fois la même capture depuis la file hors ligne. */
    cleClient: z.string().max(80).optional(),
});
/** Répondre à la question du bas : soit une option, soit une valeur précise. */
export const SchemaReponseQuestion = z.object({
    champ: SchemaChampFiche,
    /** Pour « date » : un jour AAAA-MM-JJ ou une expression ; « heure » : HH:MM ou matin/apres_midi ; « personne » : un prénom ; null = je ne sais pas encore. */
    valeur: z.string().max(200).nullable(),
});
/** Tout est éditable au doigt : chaque champ de la fiche peut être changé directement. */
export const SchemaMajFiche = z
    .object({
    titre: z.string().trim().min(1).max(200),
    type: z.enum(['tache', 'evenement', 'note', 'course']),
    categorie: SchemaCategorieElement.nullable(),
    jour: SchemaJour.nullable(),
    heure: SchemaHeure.nullable(),
    personne: z.string().trim().max(60).nullable(),
    detail: z.string().trim().max(1000).nullable(),
    visibilite: SchemaVisibilite,
    /** Ça revient ? Null = une fois. */
    cadence: SchemaCadence.nullable(),
    /** La date limite, choisie au doigt. Null = pas de limite. */
    butoir: SchemaJour.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { error: 'Rien à modifier.' });
export const SchemaCorrectionTexte = z.object({
    texte: z.string().trim().min(1, { error: 'Dis-moi ce qu’il faut changer.' }).max(1000),
});
export const SchemaValidationFiche = z.object({
    /** Les rappels du barème : cochés par défaut, décocher est plus rapide que composer. */
    rappels: z.boolean().default(true),
});
//# sourceMappingURL=captures.js.map